import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, FileText, Sheet } from "lucide-react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { RESOURCES } from "@/config/resources";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-helpers";
import EmptyState from "@/components/EmptyState";
import { EXPORTABLE, FIX_ROUTE } from "@/components/resource/constants";
import { DuplicateReview } from "@/components/resource/DuplicateReview";
import { ResourceCard } from "@/components/resource/ResourceCard";
import { TimelineDialog } from "@/components/resource/TimelineDialog";
import { ReadinessDialog } from "@/components/resource/ReadinessDialog";
import { AddResourceDialog } from "@/components/resource/AddResourceDialog";
import { PosterSuggestDialog } from "@/components/resource/PosterSuggestDialog";
import { QuoteOptionsDialog } from "@/components/resource/QuoteOptionsDialog";

export default function ResourcePage({ resourceKey }) {
  const params = useParams();
  const navigate = useNavigate();
  const [qOpts, setQOpts] = useState(null);
  const { user } = useAuth();
  const waConnected = !!user?.wa?.connect;
  const waPath = user?.role === "travel_agent" ? "/agent/whatsapp" : "/app/whatsapp";
  const key = resourceKey || params.resourceKey;
  const cfg = RESOURCES[key];
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tl, setTl] = useState(null);
  const [rd, setRd] = useState(null);
  const [rdId, setRdId] = useState(null);
  const [rdPrefs, setRdPrefs] = useState([30, 7, 1]);
  const [rdStatus, setRdStatus] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [dupes, setDupes] = useState([]);
  const [posterSuggest, setPosterSuggest] = useState(null);
  const [posterBusy, setPosterBusy] = useState(null);

  const openPosterSuggest = async (it) => {
    setPosterSuggest({ loading: true, quoteId: it.record_id });
    try {
      const { data } = await api.get(`/quotations/${it.record_id}/poster-suggestions`);
      setPosterSuggest({ ...data, quoteId: it.record_id });
    } catch (e) { toast.error("Could not load poster suggestions"); setPosterSuggest(null); }
  };
  const attachPoster = async (p) => {
    if (!posterSuggest?.quoteId) return;
    setPosterBusy(p.record_id);
    try {
      await api.post(`/quotations/${posterSuggest.quoteId}/attach-poster`, { image: p.data?.image });
      toast.success("Poster attached — it now brands the shared quotation.");
      setItems((list) => list.map((r) => r.record_id === posterSuggest.quoteId ? { ...r, data: { ...(r.data || {}), poster_image: p.data?.image } } : r));
      setPosterSuggest(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not attach the poster."); }
    finally { setPosterBusy(null); }
  };

  const loadDupes = useCallback(() => {
    if (!cfg) return;
    api.get(`/records/duplicates?type=${cfg.type}`).then((r) => setDupes(r.data.pairs || [])).catch(() => setDupes([]));
  }, [cfg]);

  const linkPair = async (p) => {
    try {
      await api.post("/records/merge", { primary_id: p.a.record_id, secondary_id: p.b.record_id });
      toast.success("Linked into one record");
      setDupes((d) => d.filter((x) => x.pair_id !== p.pair_id));
      load();
    } catch (e) { toast.error("Could not link these records"); }
  };

  const dismissPair = async (p) => {
    try { await api.post("/records/duplicates/dismiss", { pair_id: p.pair_id }); setDupes((d) => d.filter((x) => x.pair_id !== p.pair_id)); }
    catch (e) { /* silent */ }
  };

  const linkAll = async () => {
    try {
      const { data } = await api.post(`/records/duplicates/link-all?type=${cfg.type}`);
      toast.success(`Linked ${data.merged} duplicate${data.merged === 1 ? "" : "s"} into single records`);
      setDupes([]); load(); loadDupes();
    } catch (e) { toast.error("Could not link duplicates"); }
  };

  const toggleChannel = async (it, ch) => {
    const cur = (it.data?.channels) || ["in_app", "email"];
    const next = cur.includes(ch) ? cur.filter((c) => c !== ch) : [...cur, ch];
    try {
      await api.patch(`/records/${it.record_id}`, { data: { ...(it.data || {}), channels: next } });
      setItems((x) => x.map((y) => y.record_id === it.record_id ? { ...y, data: { ...(y.data || {}), channels: next } } : y));
    } catch (e) { toast.error("Could not update notification channel"); }
  };
  const waReminder = (it) => {
    if (!waConnected) { toast.info("Connect WhatsApp to send from here — opening WhatsApp setup."); navigate(waPath); return; }
    const due = it.data?.due_date ? ` (due ${String(it.data.due_date).slice(0, 10)})` : "";
    window.open(`https://wa.me/?text=${encodeURIComponent(`Reminder: ${it.title}${due}`)}`, "_blank");
  };
  const snooze = async (it) => {
    try {
      const { data } = await api.post(`/records/${it.record_id}/snooze`, { days: 7 });
      toast.success(`Snoozed to ${data.due_date}`);
      load();
    } catch (e) { toast.error("Could not snooze this reminder"); }
  };

  const load = useCallback(async () => {
    if (!cfg) return;
    try {
      const { data } = await api.get(`/records?type=${cfg.type}`);
      setItems(data.records);
      if (cfg.readiness) {
        data.records.forEach((it) => {
          api.get(`/trips/${it.record_id}/readiness`).then((r) => setRdStatus((s) => ({ ...s, [it.record_id]: { status: r.data.status, ok: r.data.score.ok, total: r.data.score.total } }))).catch(() => {});
        });
      }
    } catch (e) { setItems([]); }
  }, [cfg]);
  useEffect(() => { setItems(null); setDupes([]); load(); loadDupes(); }, [key, load, loadDupes]);

  if (!cfg) return <div className="p-6 text-slate-500">Unknown section.</div>;

  const openAdd = () => { setForm({}); setAiBrief(""); setOpen(true); };
  const exportModule = async (fmt) => {
    try { await downloadFile(`/reports/export?scope=${cfg.type}&fmt=${fmt}`, `${cfg.type}_report.${fmt}`); toast.success(`${fmt.toUpperCase()} downloaded`); }
    catch (e) { toast.error("Could not export this report."); }
  };

  const exportQuotePdf = async (it) => {
    try { await downloadFile(`/quotations/${it.record_id}/pdf`, `${(it.title || "quotation").replace(/\s+/g, "_")}.pdf`); toast.success("Quotation PDF downloaded"); }
    catch (e) { toast.error("Could not generate the quotation PDF."); }
  };

  const aiDraft = async () => {
    if (aiBrief.trim().length < 4) { toast.error("Add a short brief first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/draft", { kind: "quotation", brief: aiBrief });
      const d = data.draft || {};
      const inclusions = [d.inclusions, d.itinerary].filter(Boolean).join("\n\n");
      setForm((f) => ({ ...f, title: d.title || f.title, destination: d.destination || f.destination, inclusions: inclusions || f.inclusions }));
      toast.success("AI draft ready — review and save");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const save = async () => {
    const first = cfg.fields[0];
    if (first.required && !form[first.key]) { toast.error(`${first.label} is required.`); return; }
    setSaving(true);
    const data = {};
    cfg.fields.forEach((f) => { if (f.key !== "title" && form[f.key] !== undefined) data[f.key] = form[f.key]; });
    const subKeys = cfg.fields.filter((f) => ["category", "provider", "destination", "package", "plan", "relation", "phone"].includes(f.key));
    const subtitle = subKeys.map((f) => form[f.key]).filter(Boolean).join(" · ");
    try {
      await api.post("/records", {
        type: cfg.type,
        title: form[cfg.fields[0].key] || "Untitled",
        subtitle,
        status: form.status || (cfg.statuses ? cfg.statuses[0] : "active"),
        data,
        source: "Manual Entry",
        verification: "customer_confirmed",
      });
      toast.success(`${cfg.title.replace(/s$/, "")} saved`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/records/${id}`); toast.success("Deleted"); setItems((it) => it.filter((x) => x.record_id !== id)); }
    catch (e) { toast.error("Could not delete"); }
  };

  const share = async (it) => {
    try {
      const { data } = await api.post(`/quotations/${it.record_id}/share`);
      const url = `${window.location.origin}/q/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (e) {}
      toast.success("Share link copied", { description: url });
      setItems((list) => list.map((r) => r.record_id === it.record_id && r.status === "draft" ? { ...r, status: "sent" } : r));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openTimeline = async (it) => {
    try { const { data } = await api.get(`/orders/${it.record_id}/timeline`); setTl(data); }
    catch (e) { toast.error("Could not load timeline"); }
  };
  const openReadiness = async (it) => {
    try {
      const { data } = await api.get(`/trips/${it.record_id}/readiness`); setRd(data); setRdId(it.record_id);
      api.get("/me/alert-preferences").then((r) => setRdPrefs(r.data.milestones || [])).catch(() => {});
    } catch (e) { toast.error("Could not load readiness"); }
  };
  const togglePref = async (m) => {
    const next = rdPrefs.includes(m) ? rdPrefs.filter((x) => x !== m) : [...rdPrefs, m];
    setRdPrefs(next);
    try { await api.put("/me/alert-preferences", { milestones: next }); } catch (e) {}
  };
  const openItinerary = async () => {
    try { const { data } = await api.post(`/trips/${rdId}/share-readiness`); window.open(`${window.location.origin}/trip-check/${data.token}`, "_blank", "noopener"); }
    catch (e) { toast.error("Could not open itinerary"); }
  };
  const shareReadiness = async () => {
    try {
      const { data } = await api.post(`/trips/${rdId}/share-readiness`);
      const url = `${window.location.origin}/trip-check/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch (e) {}
      toast.success("Checklist link copied", { description: url });
    } catch (e) { toast.error("Could not create share link"); }
  };
  const sendQuote = async (it) => {
    try {
      const { data } = await api.post(`/quotations/${it.record_id}/share`);
      const link = `${window.location.origin}/q/${data.token}`;
      const price = it.data?.customer_price ? ` (₹${Number(it.data.customer_price).toLocaleString("en-IN")})` : "";
      if (!waConnected) {
        try { await navigator.clipboard.writeText(link); } catch (e) {}
        toast.info("Connect WhatsApp to send — your quote link is copied. Opening WhatsApp setup.");
        navigate(waPath);
        return;
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(`Here is your travel quotation: ${it.title}${price}\n${link}`)}`, "_blank", "noopener");
      navigate(`/agent/messages?quote=${it.record_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sellPrice = (r) => {
    const net = Number(r.data?.net_cost) || 0;
    if (r.data?.sell_price) return Number(r.data.sell_price);
    const mv = Number(r.data?.markup_value) || 0;
    return r.data?.markup_type === "fixed" ? net + mv : Math.round(net * (1 + mv / 100));
  };
  const createQuoteFromRate = async (it) => {
    const price = sellPrice(it);
    try {
      await api.post("/records", {
        type: "quotation", title: it.title, subtitle: it.data?.supplier || "", status: "draft",
        data: { destination: "", customer_price: price, net_cost: Number(it.data?.net_cost) || 0, inclusions: it.data?.notes || "" },
        source: "Rate Library", verification: "customer_confirmed",
      });
      toast.success("Draft quotation created", { description: `Sell price ₹${price.toLocaleString("en-IN")}` });
      navigate("/agent/quotations");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} icon={cfg.icon}
        actions={<div className="flex items-center gap-2">
          {EXPORTABLE.has(cfg.type) && (
            <div className="hidden items-center gap-1 sm:flex" data-testid={`export-${key}`}>
              <button onClick={() => exportModule("pdf")} title="Export as PDF" data-testid={`export-${key}-pdf`}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-rose-300 hover:text-rose-500"><FileText className="h-3.5 w-3.5" /> PDF</button>
              <button onClick={() => exportModule("xlsx")} title="Export as Excel" data-testid={`export-${key}-xlsx`}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-emerald-300 hover:text-emerald-600"><Sheet className="h-3.5 w-3.5" /> Excel</button>
            </div>
          )}
          <button onClick={openAdd} data-testid={`add-${key}-btn`}
            className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90">
            <Plus className="h-4 w-4" /> {cfg.addLabel}</button>
        </div>} />

      <DuplicateReview dupes={dupes} onLinkAll={linkAll} onLinkPair={linkPair} onDismiss={dismissPair} />

      {items === null ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-white/70" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={cfg.icon} title={cfg.empty}
          description="Add or import records. Suffle only shows your own authorized data — never fabricated information."
          action={<div className="flex flex-wrap items-center justify-center gap-2">
            <button onClick={openAdd} className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> {cfg.addLabel}</button>
            <a href="/app/connected-apps" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:border-blue-300">Connect an app</a>
          </div>}
          testid={`empty-${key}`} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`${key}-list`}>
          {items.map((it, i) => (
            <ResourceCard key={it.record_id} it={it} cfg={cfg} kk={key} index={i} rd={rdStatus[it.record_id]}
              onTimeline={openTimeline} onReadiness={openReadiness} onQuoteFrom={createQuoteFromRate} onQuoteSend={sendQuote}
              onShare={share} onDelete={remove} onToggleChannel={toggleChannel} onWaReminder={waReminder} onSnooze={snooze} onQuotePoster={openPosterSuggest} onQuoteOptions={setQOpts} onQuotePdf={exportQuotePdf} />
          ))}
        </div>
      )}

      <TimelineDialog tl={tl} onClose={() => setTl(null)} />

      {qOpts && <QuoteOptionsDialog it={qOpts} onClose={() => { setQOpts(null); load(); }} />}
      <ReadinessDialog rd={rd} onClose={() => setRd(null)} rdPrefs={rdPrefs} onTogglePref={togglePref}
        onItinerary={openItinerary} onShare={shareReadiness} showPreview={showPreview} setShowPreview={setShowPreview}
        onFix={(k) => { setRd(null); navigate(FIX_ROUTE[k]); }} />

      <AddResourceDialog open={open} onOpenChange={setOpen} cfg={cfg} kk={key} form={form} setForm={setForm}
        onSave={save} saving={saving} aiBrief={aiBrief} setAiBrief={setAiBrief} onAiDraft={aiDraft} aiLoading={aiLoading} />

      <PosterSuggestDialog data={posterSuggest && !posterSuggest.loading ? posterSuggest : null}
        busyId={posterBusy} onClose={() => setPosterSuggest(null)} onAttach={attachPoster} />
    </div>
  );
}
